import React, { useCallback, useMemo, useState } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import { Loader, X, AlertTriangle } from 'lucide-react';

const containerStyle = {
    width: '100%',
    height: '100%'
};

// Default center (Mexico City)
const center = {
    lat: 19.4326,
    lng: -99.1332
};

const libraries = ['geometry'];

const MapModal = ({ isOpen, onClose, trackData, alarmData, loading, error, mapApiKey }) => {
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: mapApiKey,
        libraries
    });

    const [map, setMap] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const onLoad = useCallback(function callback(map) {
        setMap(map);
    }, []);

    const onUnmount = useCallback(function callback(map) {
        setMap(null);
    }, []);

    // Decode polylines and fit bounds
    const decodedPaths = useMemo(() => {
        if (!trackData || !window.google) return [];

        const paths = [];
        const bounds = new window.google.maps.LatLngBounds();
        let hasPoints = false;

        trackData.forEach(segment => {
            try {
                const decoded = window.google.maps.geometry.encoding.decodePath(segment.polyline);
                paths.push(decoded);
                decoded.forEach(point => bounds.extend(point));
                if (decoded.length > 0) hasPoints = true;
            } catch (err) {
                console.error('Error decoding polyline', err);
            }
        });

        if (map && hasPoints) {
            map.fitBounds(bounds);
        }

        return paths;
    }, [trackData, map]);

    // Process alarms and fit bounds
    const alarmMarkers = useMemo(() => {
        if (!alarmData || !window.google) return [];

        const markers = [];
        const bounds = new window.google.maps.LatLngBounds();
        let hasPoints = false;

        alarmData.forEach(alarm => {
            if (alarm.location && alarm.location.lat && alarm.location.lng) {
                const position = { lat: alarm.location.lat, lng: alarm.location.lng };
                markers.push({ ...alarm, position });
                bounds.extend(position);
                hasPoints = true;
            }
        });

        // Only fit bounds if we have alarm points and NO track points (to avoid conflict or double fitting)
        // Or we could extend the existing bounds if we tracked them outside. 
        // For simplicity, if we have alarms, we fit bounds to them if no track is present, 
        // or we expect the map to handle it if we extended current bounds.
        // Actually, best strictly for this use case: if we are viewing alarms, trackData often empty.
        if (map && hasPoints) {
            map.fitBounds(bounds);
        }

        return markers;
    }, [alarmData, map]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden relative">

                {/* Header */}
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-white dark:bg-slate-800 z-10">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Route Map</h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 relative bg-slate-100 dark:bg-slate-900">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 z-20">
                            <Loader className="w-8 h-8 animate-spin text-indigo-500" />
                        </div>
                    )}

                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                            <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl border border-red-200 dark:border-red-800 text-center">
                                <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                                <p className="text-red-800 dark:text-red-200">{error}</p>
                            </div>
                        </div>
                    )}

                    {isLoaded ? (
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={center}
                            zoom={10}
                            onLoad={onLoad}
                            onUnmount={onUnmount}
                            options={{
                                styles: [
                                    {
                                        "featureType": "all",
                                        "elementType": "geometry.fill",
                                        "stylers": [
                                            {
                                                "weight": "2.00"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "all",
                                        "elementType": "geometry.stroke",
                                        "stylers": [
                                            {
                                                "color": "#9c9c9c"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "all",
                                        "elementType": "labels.text",
                                        "stylers": [
                                            {
                                                "visibility": "on"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "landscape",
                                        "elementType": "all",
                                        "stylers": [
                                            {
                                                "color": "#f2f2f2"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "landscape",
                                        "elementType": "geometry.fill",
                                        "stylers": [
                                            {
                                                "color": "#ffffff"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "landscape.man_made",
                                        "elementType": "geometry.fill",
                                        "stylers": [
                                            {
                                                "color": "#ffffff"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "poi",
                                        "elementType": "all",
                                        "stylers": [
                                            {
                                                "visibility": "off"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "road",
                                        "elementType": "all",
                                        "stylers": [
                                            {
                                                "saturation": -100
                                            },
                                            {
                                                "lightness": 45
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "road",
                                        "elementType": "geometry.fill",
                                        "stylers": [
                                            {
                                                "color": "#eeeeee"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "road",
                                        "elementType": "labels.text.fill",
                                        "stylers": [
                                            {
                                                "color": "#7b7b7b"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "road",
                                        "elementType": "labels.text.stroke",
                                        "stylers": [
                                            {
                                                "color": "#ffffff"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "road.highway",
                                        "elementType": "all",
                                        "stylers": [
                                            {
                                                "visibility": "simplified"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "road.arterial",
                                        "elementType": "labels.icon",
                                        "stylers": [
                                            {
                                                "visibility": "off"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "transit",
                                        "elementType": "all",
                                        "stylers": [
                                            {
                                                "visibility": "off"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "water",
                                        "elementType": "all",
                                        "stylers": [
                                            {
                                                "color": "#46bcec"
                                            },
                                            {
                                                "visibility": "on"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "water",
                                        "elementType": "geometry.fill",
                                        "stylers": [
                                            {
                                                "color": "#c8d7d4"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "water",
                                        "elementType": "labels.text.fill",
                                        "stylers": [
                                            {
                                                "color": "#070707"
                                            }
                                        ]
                                    },
                                    {
                                        "featureType": "water",
                                        "elementType": "labels.text.stroke",
                                        "stylers": [
                                            {
                                                "color": "#ffffff"
                                            }
                                        ]
                                    }
                                ]
                            }}
                        >
                            {decodedPaths.map((path, idx) => (
                                <Polyline
                                    key={idx}
                                    path={path}
                                    options={{
                                        strokeColor: '#3b82f6', // Indigo/Blue
                                        strokeOpacity: 0.8,
                                        strokeWeight: 4,
                                    }}
                                />
                            ))}

                            {/* Start Marker (Green) */}
                            {decodedPaths.length > 0 && decodedPaths[0].length > 0 && (
                                <Marker
                                    position={decodedPaths[0][0]}
                                    label={{ text: 'A', color: 'white', fontWeight: 'bold' }}
                                />
                            )}

                            {decodedPaths.length > 0 && decodedPaths[decodedPaths.length - 1].length > 0 && (
                                <Marker
                                    position={decodedPaths[decodedPaths.length - 1][decodedPaths[decodedPaths.length - 1].length - 1]}
                                    label={{ text: 'B', color: 'white', fontWeight: 'bold' }}
                                />
                            )}

                            {/* Alarm Markers */}
                            {alarmMarkers.map((alarm, idx) => (
                                <Marker
                                    key={idx}
                                    position={alarm.position}
                                    onClick={() => setSelectedMarker(alarm)}
                                    icon={
                                        alarm.type === 'SOS alarm'
                                            ? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                                            : alarm.type === 'Prepanic alarm'
                                                ? 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
                                                : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                                    }
                                />
                            ))}

                            {/* Info Window */}
                            {selectedMarker && (
                                <InfoWindow
                                    position={selectedMarker.position}
                                    onCloseClick={() => setSelectedMarker(null)}
                                >
                                    <div className="p-2 min-w-[200px]">
                                        <h4 className="font-bold text-slate-900 mb-1">{selectedMarker.type}</h4>
                                        <p className="text-sm text-slate-600 mb-1">
                                            {new Date(selectedMarker.date * 1000).toLocaleString()}
                                        </p>
                                        <div className="text-xs text-slate-400 font-mono">
                                            {selectedMarker.position.lat.toFixed(5)}, {selectedMarker.position.lng.toFixed(5)}
                                        </div>
                                    </div>
                                </InfoWindow>
                            )}

                        </GoogleMap>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <Loader className="w-8 h-8 animate-spin text-slate-400" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(MapModal);
